<template>
  <div id="page-beneficiary-edit">
    <div class="vx-row">
      <div v-if="beneficiaryData" class="vx-col w-full mb-base">
        <beneficiary-form
          ref="beneficiaryForm"
          v-model="beneficiaryData"
          :loading="loading"
          card-title="Details"
        />
        <div class="vx-row">
          <div class="vx-col sm:w-1/2">
            <div class="mt-8 flex flex-wrap items-center justify-start">
              <vs-button
                :disabled="loading"
                class="my-4"
                @click="cancel"
              >
                Cancel
              </vs-button>
            </div>
          </div>
          <div class="vx-col sm:w-1/2">
            <div class="mt-8 flex flex-wrap items-center justify-end">
              <vs-button
                :disabled="loading"
                class="my-4"
                @click="save"
                color="light"
              >
                Save
              </vs-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BeneficiaryForm from '@/views/pages/user/beneficiaries/BeneficiaryForm';
import NotificationMixin from '@/components/mixins/NotificationMixin';
import FormValidateMixin from '@/components/mixins/FormValidateMixin';
import { EMPTY_BENEFICIARY_MODEL } from '@/views/pages/user/beneficiaries/constants';

export default {
  name: 'BeneficiaryEdit',
  components: {
    BeneficiaryForm,
  },
  mixins: [NotificationMixin, FormValidateMixin],
  data() {
    return {
      loading: false,
      beneficiaryData: { ...EMPTY_BENEFICIARY_MODEL },
      existingBeneficiaryId: null,
    };
  },
  computed: {
    userId() {
      return this.$route.params.id;
    },
  },
  methods: {
    /**
     * Validate the form before saving.
     * @returns {boolean}
     */
    async validateSave() {
      if (!this.$refs.beneficiaryForm || !this.$refs.beneficiaryForm.validate()) {
        return false;
      }
      return true;
    },

    /**
     * Extract form data for API submission.
     * @returns {Object}
     */
    getParams() {
      return {
        BeneficiaryFirstName: this.beneficiaryData.BeneficiaryFirstName,
        BeneficiaryLastName: this.beneficiaryData.BeneficiaryLastName,
        BeneficiaryEmailAddress: this.beneficiaryData.BeneficiaryEmailAddress,
        BeneficiaryBankAccountName: this.beneficiaryData.BeneficiaryBankAccountName,
        iban: this.beneficiaryData.iban,
      };
    },

    /**
     * Save beneficiary (create or update).
     */
    async save() {
      if (!await this.validateSave()) {
        this.$vs.notify({
          color: 'warning',
          title: 'Validation Error',
          text: 'Please fill in all required fields',
          position: 'top-right',
        });
        return false;
      }

      this.loading = true;
      const params = this.getParams();

      try {
        if (this.existingBeneficiaryId) {
          await this.updateBeneficiary(params);
        } else {
          await this.createBeneficiary(params);
        }

        await this.navigateToView();
      } catch (error) {
        this.handleSaveError(error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Update existing beneficiary.
     * @param {Object} params
     */
    async updateBeneficiary(params) {
      await this.$api.users.beneficiaries.update(this.userId, this.existingBeneficiaryId, params);
      this.$vs.notify({
        color: 'success',
        title: 'Beneficiary updated',
        text: 'The beneficiary was successfully updated',
        position: 'top-right',
      });
    },

    /**
     * Create new beneficiary.
     * @param {Object} params
     */
    async createBeneficiary(params) {
      await this.$api.users.beneficiaries.create(this.userId, params);
      this.$vs.notify({
        color: 'success',
        title: 'Beneficiary created',
        text: 'The beneficiary was successfully created',
        position: 'top-right',
      });
    },

    /**
     * Handle save errors.
     * @param {Error} error
     */
    handleSaveError(error) {
      this.addErrors(error);
      if (error.response && error.response.status !== 422) {
        const errorMessage = (error.response && error.response.data && error.response.data.message)
          ? error.response.data.message
          : 'Not able to save beneficiary. Please try again.';
        
        this.$vs.notify({
          color: 'danger',
          title: 'Error occurred',
          text: errorMessage,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'top-right',
        });
      }
    },

    /**
     * Navigate back to view screen.
     */
    async navigateToView() {
      await this.$router.push({
        name: 'contact.beneficiaries.view',
        params: { id: this.userId },
      });
    },

    /**
     * Cancel editing and return to view screen.
     */
    cancel() {
      this.$router.push({
        name: 'contact.beneficiaries.view',
        params: { id: this.userId },
      });
    },

    /**
     * Fetch beneficiary data for the current user.
     * If no beneficiary exists, initializes with empty values.
     */
    async fetch() {
      try {
        const beneficiary = await this.$api.users.beneficiaries.getSingle(this.userId);
        
        if (beneficiary) {
          this.beneficiaryData = beneficiary;
          this.existingBeneficiaryId = beneficiary.id;
        } else {
          this.beneficiaryData = { ...EMPTY_BENEFICIARY_MODEL };
          this.existingBeneficiaryId = null;
        }
      } catch (error) {
        console.error('Error fetching beneficiary:', error);
        this.$vs.notify({
          color: 'danger',
          title: 'Error',
          text: 'Failed to load beneficiary details',
          position: 'top-right',
        });
        // Initialize with empty values on error
        this.beneficiaryData = { ...EMPTY_BENEFICIARY_MODEL };
        this.existingBeneficiaryId = null;
      }
    },
  },
  async mounted() {
    await this.fetch();
  },
};
</script>

<style scoped>
</style>
